import { useAppSelector } from "@hooks";
import { lazy, ReactElement } from "react";

import { Navigate, useLocation, useRoutes } from "react-router-dom";

import AppLayout from "./layouts/app";
import LogoOnlyLayout from "./layouts/LogoOnlyLayout";

/**
 * Sayfalar lazy yükleniyor: CKEditor, apexcharts ve @react-pdf/renderer gibi
 * ağır bağımlılıklar ilk açılışta değil, ilgili sayfaya girilince indiriliyor.
 */
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Categories = lazy(() => import("./pages/Categories"));
const Users = lazy(() => import("./pages/Users"));
const UserInfo = lazy(() => import("./pages/Users/edit"));
const Kyc = lazy(() => import("./pages/Kyc"));
const Logs = lazy(() => import("./pages/Logs"));
const Products = lazy(() => import("./pages/Products"));
const ProductEdit = lazy(() => import("./pages/Products/Edit"));
const Pages = lazy(() => import("./pages/Pages"));
const ListingFees = lazy(() => import("./pages/ListingFees"));
const Contact = lazy(() => import("./pages/Contact"));
const Login = lazy(() => import("./pages/Login"));
const PageNotFound = lazy(() => import("./pages/PageNotFound"));

/**
 * Oturum kontrolünü render sırasında yapar. useEffect ile yönlendirme yapılırsa
 * korumalı sayfa bir kere render olur ve yetkisiz API çağrılarını tetikler.
 */
function RequireAuth({ children }: { children: ReactElement }) {
  const { token, isLoggedIn } = useAppSelector((state) => state.auth);
  const location = useLocation();

  if (!isLoggedIn || !token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
}

/**
 * Oturum açıkken login sayfasına gelinirse panele geri gönderir.
 */
function RedirectIfAuthed({ children }: { children: ReactElement }) {
  const { token, isLoggedIn } = useAppSelector((state) => state.auth);

  if (isLoggedIn && token) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default function Router() {
  return useRoutes([
    {
      path: "/",
      element: (
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <Navigate to="/dashboard" replace /> },
        { path: "dashboard", element: <Dashboard /> },
        { path: "categories", element: <Categories /> },
        { path: "users", element: <Users /> },
        { path: "users/:id", element: <UserInfo /> },
        { path: "kyc", element: <Kyc /> },
        { path: "logs", element: <Logs /> },
        { path: "products", element: <Products /> },
        { path: "products/:id", element: <ProductEdit /> },
        { path: "pages", element: <Pages /> },
        { path: "listing-fees", element: <ListingFees /> },
        { path: "contact", element: <Contact /> },
      ],
    },
    {
      element: <LogoOnlyLayout />,
      children: [
        {
          path: "login",
          element: (
            <RedirectIfAuthed>
              <Login />
            </RedirectIfAuthed>
          ),
        },
        { path: "404", element: <PageNotFound /> },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}
